import { useEffect, useRef, type ReactNode } from "react";

interface ScrollRevealProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  direction?: "up" | "down" | "left" | "right";
  duration?: number;
  threshold?: number;
  once?: boolean;
}

export default function ScrollReveal({
  children,
  className = "",
  delay = 0,
  direction = "up",
  duration = 700,
  threshold = 0.15,
  once = true,
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);

  const translateMap = {
    up: "translate-y-10",
    down: "-translate-y-10",
    left: "translate-x-10",
    right: "-translate-x-10",
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              el.classList.remove("opacity-0", translateMap[direction]);
              el.classList.add("opacity-100", "translate-x-0", "translate-y-0");
            }, delay);
            if (once) observer.unobserve(el);
          } else if (!once) {
            el.classList.add("opacity-0", translateMap[direction]);
            el.classList.remove("opacity-100", "translate-x-0", "translate-y-0");
          }
        });
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [delay, direction, threshold, once]);

  const translateClass = translateMap[direction];

  return (
    <div
      ref={ref}
      className={`opacity-0 ${translateClass} transition-all ease-out will-change-transform ${className}`}
      style={{ transitionDuration: `${duration}ms`, willChange: "transform, opacity" }}
    >
      {children}
    </div>
  );
}